<template>
  <div class="app-container" :class="{ 'screenshot-mode': screenshotMode }">
    <el-card class="box-card no-print">
      <div class="filter-container">
        <el-form :model="query" ref="queryForm" :inline="true">
          <el-form-item label="子账号：" prop="keywords">
            <el-input v-model="query.keyword" placeholder="请输入姓名/手机号" size="small" clearable @keyup.enter.native="handleQuery" />
          </el-form-item>
          <el-form-item label="时间类型：">
            <el-select v-model="query.timeType" placeholder="请选择" size="small" style="width:110px">
              <el-option label="新增" :value="1" />
              <el-option label="审核通过" :value="2" />
              <el-option label="回访" :value="3" />
              <el-option label="市场核实" :value="4" />
            </el-select>
          </el-form-item>
          <el-form-item label="日期：">
            <el-radio-group v-model="quickRange" size="small" @change="handleQuickRangeChange">
              <!-- 测试单 181：要能"不按时间筛、看全部"。选「全部」= 不传起止时间，后端就不加时间条件 -->
              <el-radio-button label="all">全部</el-radio-button>
              <el-radio-button label="last7">近7天</el-radio-button>
              <el-radio-button label="thisWeek">本周</el-radio-button>
              <el-radio-button label="lastWeek">上周</el-radio-button>
              <el-radio-button label="thisMonth">本月</el-radio-button>
              <el-radio-button label="lastMonth">上月</el-radio-button>
              <el-radio-button label="custom">自定义</el-radio-button>
            </el-radio-group>
            <el-date-picker
              v-if="quickRange === 'custom'"
              v-model="customDateRange"
              type="daterange"
              size="small"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              style="margin-left:8px"
              @change="handleCustomRangeChange"
            />
          </el-form-item>
          <el-form-item class="fr">
            <el-button type="primary" @click="handleQuery" size="mini">搜索</el-button>
            <el-button type="info" @click="handleReset" plain size="mini">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="range-tip" v-if="quickRange === 'all'">当前查询区间：全部时间</div>
        <div class="range-tip" v-else>当前查询区间：{{ query.startTime || '—' }} 至 {{ query.endTime || '—' }}（按「{{ timeTypeText }}」时间）</div>
      </div>
    </el-card>

    <el-card class="box-card mt10">
      <!-- 6 个汇总指标，与列表同口径同步刷新 -->
      <el-row :gutter="12" class="summary-row">
        <el-col :span="4" v-for="item in summaryCards" :key="item.status">
          <div class="summary-card" :class="{ active: activeStatus === item.status }" @click="switchStatus(item.status)">
            <div class="summary-value">{{ summary[item.field] || 0 }}</div>
            <div class="summary-label">{{ item.label }}</div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <el-card class="box-card mt10">
      <div class="btn-right-side mb10 no-print">
        <el-tabs v-model="activeStatus" @tab-click="doQueryList({ pageNo: 1 })">
          <el-tab-pane label="全部" name="0" />
          <el-tab-pane label="待回访" name="1" />
          <el-tab-pane label="回访正常" name="2" />
          <el-tab-pane label="异常已关闭" name="3" />
          <el-tab-pane label="待市场核实" name="4" />
          <el-tab-pane label="市场核实完成" name="5" />
        </el-tabs>
        <div class="btn-box">
          <!-- 市场核实接收人：后端接口早就有，但一直没有页面，运营配不了 -->
          <el-button v-if="canSetReceivers" size="mini" plain class="no-print" @click="$refs.marketReceivers.open()">接收人设置</el-button>
          <el-button type="primary" size="mini" plain @click="screenshotMode = !screenshotMode">
            {{ screenshotMode ? '退出截图模式' : '周报截图模式' }}
          </el-button>
          <el-button type="primary" size="mini" @click="handleExport">导出</el-button>
        </div>
      </div>

      <el-table v-loading="listLoading" :data="list" :key="activeStatus">
        <el-table-column label="子账号" align="center" min-width="140">
          <template slot-scope="{ row }">
            <div>{{ row.childName }}</div>
            <masked-phone :customer-id="row.childCustomerId" :followup-id="row.id" :masked="row.childPhoneMasked" />
          </template>
        </el-table-column>
        <el-table-column label="主账号" align="center" min-width="140">
          <template slot-scope="{ row }">
            <div>{{ row.parentName }}</div>
            <masked-phone :customer-id="row.parentCustomerId" :followup-id="row.id" :masked="row.parentPhoneMasked" />
          </template>
        </el-table-column>
        <el-table-column label="申请来源" align="center">
          <template slot-scope="{ row }">{{ row.applySourceDesc || '—' }}</template>
        </el-table-column>
        <el-table-column label="所属大区" prop="regionName" align="center">
          <template slot-scope="{ row }">{{ row.regionName || '—' }}</template>
        </el-table-column>
        <el-table-column label="省市区" align="center" min-width="140">
          <!-- province/city/area 是地区编码（62 / 6206 / 620602），给人看的是 *Name -->
          <template slot-scope="{ row }">{{ [row.provinceName, row.cityName, row.areaName].filter(i => i).join('') || '—' }}</template>
        </el-table-column>
        <el-table-column label="身份等级" align="center">
          <template slot-scope="{ row }">{{ row.identityLevel || '—' }}</template>
        </el-table-column>
        <el-table-column label="状态" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.status)">{{ statusText(row.status) }}</el-tag>
            <div v-if="row.subStatus && row.subStatus !== 0" class="sub-status">{{ subStatusText(row.subStatus) }}</div>
          </template>
        </el-table-column>
        <!-- 测试单 171/179：缺 联系结果 / 跟进人 / 跟进反馈 -->
        <el-table-column label="联系结果" align="center" width="90">
          <template slot-scope="{ row }">{{ contactResultText(row.contactResult) }}</template>
        </el-table-column>
        <el-table-column label="跟进人" align="center" width="100">
          <template slot-scope="{ row }">{{ row.visitByName || '—' }}</template>
        </el-table-column>
        <el-table-column label="跟进反馈" align="left" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.feedback || '—' }}</template>
        </el-table-column>
        <!-- 「新增时间」= 子账号申请的提交时间（与"时间类型：新增"同一个口径），不是回访任务这一行的创建时间 -->
        <el-table-column label="新增时间" prop="submitTime" align="center" width="160" />
        <el-table-column label="审核通过时间" prop="auditTime" align="center" width="160" />
        <!-- REQ-2026-601171 #4：回访时间显示的是教育部/客服在表单里填的 visitTime（"什么时候联系的客户"），不是提交时刻 visitAt；老数据没有 visitTime 时回落 visitAt，不显示空白。 -->
        <el-table-column label="回访时间" align="center" width="160">
          <template slot-scope="{ row }">{{ row.visitTime || row.visitAt || '—' }}</template>
        </el-table-column>
        <!-- BUG-2026-718861：未接通回访后，列表上看不出约了哪天再打。
             nextFollowAt 后端一直在返（listColumns 里有这一列），只是前端从来没用过。 -->
        <el-table-column label="下次跟进时间" align="center" width="160">
          <template slot-scope="{ row }">{{ row.nextFollowAt || '—' }}</template>
        </el-table-column>
        <el-table-column label="市场核实时间" prop="marketVerifiedAt" align="center" width="160" />
        <el-table-column label="操作" align="center" fixed="right" width="220" class-name="no-print" v-if="!screenshotMode">
          <template slot-scope="{ row }">
            <el-button v-if="row.status === 1" type="text" @click="goVisit(row.id)">填写回访</el-button>
            <el-button v-else type="text" @click="goShow(row.id)">查看详情</el-button>
            <el-button v-if="[3, 4, 5].includes(row.status) || row.contactResult" type="text" @click="goActivities(row.id)">流转记录</el-button>
            <el-button v-if="[3, 4, 5].includes(row.status)" type="text" @click="goCloseRecord(row.id)">关闭记录</el-button>
          </template>
        </el-table-column>
      </el-table>

      <pagination v-show="total > 0" :total="total" :pageNo.sync="query.pageNo" :pageSize.sync="query.pageSize" @pagination="doQueryList" class="no-print" />
    </el-card>
    <market-receivers ref="marketReceivers" />
  </div>
</template>

<script>
import childFollowupApi from '@/api/cust/childFollowup'
import Pagination from '@/components/Pagination'
import MaskedPhone from './components/MaskedPhone.vue'
import MarketReceivers from './components/MarketReceivers.vue'

const STATUS_MAP = { 1: '待回访', 2: '回访正常', 3: '异常已关闭', 4: '待市场核实', 5: '市场核实完成' }
const STATUS_TAG = { 1: 'warning', 2: 'success', 3: 'info', 4: 'warning', 5: '' }
const SUB_STATUS_MAP = { 1: '确认异常', 2: '误判已澄清', 3: '仍无法核实' }

export default {
  name: 'SubaccountFollowupList',
  components: { Pagination, MaskedPhone, MarketReceivers },
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      activeStatus: '0',
      quickRange: 'last7',
      customDateRange: null,
      screenshotMode: false,
      query: {
        pageNo: 1,
        pageSize: 10,
        // ⚠️ 参数名是 keyword（后端 CustomerChildFollowupQO.keyword）。原来写成了 keywords，
        //    后端收不到 → 关键字搜索**静默不生效**（永远返回全部），看起来像"搜了但没筛"
        keyword: '',
        // ⚠️ 默认按「审核通过」时间筛，不能是「回访」：待回访的任务 visit_at 是 NULL，
        //    按回访时间筛会把它们**全部筛掉** —— 测试单 180"有数据但列表空白"就是这个默认值造成的，
        //    而"待回访"恰恰是这张列表最主要的用途
        timeType: 2,
        startTime: '',
        endTime: ''
      },
      // 6 个汇总指标：与状态 tab 同口径。
      // ⚠️ 键名**必须和后端 CustomerChildFollowupMetricsVO 一字不差**
      //    （BUG-2026-628461：原来前端自己起了 totalCount/waitVisitCount… 一套名字，
      //     接口返回的是 total/pending/…，于是 6 个卡片恒为 0 且不报错）。
      //    要改名去改后端 VO，两边一起改，不要只改这里。
      summary: {
        total: 0,
        pending: 0,
        normal: 0,
        closed: 0,
        marketPending: 0,
        marketDone: 0
      },
      summaryCards: [
        { status: '0', field: 'total', label: '全部' },
        { status: '1', field: 'pending', label: '待回访' },
        { status: '2', field: 'normal', label: '回访正常' },
        { status: '3', field: 'closed', label: '异常已关闭' },
        { status: '4', field: 'marketPending', label: '待市场核实' },
        { status: '5', field: 'marketDone', label: '市场核实完成' }
      ]
    }
  },
  watch: {
    // 周报截图模式：隐藏导航与操作列，保留周期/指标/明细
    // sidebar/navbar 是 Layout 的兄弟节点，不在本组件 DOM 树内，只能靠 body 上挂全局 class 控制
    screenshotMode(val) {
      document.body.classList.toggle('followup-screenshot-mode', val)
    }
  },
  computed: {
    canSetReceivers() {
      // 权限点与增量 SQL / 后端 checkPermission 同名
      const permissions = (this.$store.getters && this.$store.getters.permissions) || []
      return permissions.includes('*:*:*') || permissions.includes('finance:subaccountFollowup:marketReceivers')
    },
    timeTypeText() {
      return { 1: '新增', 2: '审核通过', 3: '回访', 4: '市场核实' }[this.query.timeType] || ''
    }
  },
  created() {
    this.applyQuickRange('last7')
    this.doQueryList({ pageNo: 1 })
  },
  beforeDestroy() {
    document.body.classList.remove('followup-screenshot-mode')
  },
  methods: {
    statusText(status) {
      return STATUS_MAP[status] || '—'
    },
    statusTagType(status) {
      return STATUS_TAG[status] || ''
    },
    subStatusText(subStatus) {
      return SUB_STATUS_MAP[subStatus] || ''
    },
    switchStatus(status) {
      this.activeStatus = status
      this.doQueryList({ pageNo: 1 })
    },
    // 日期快捷项：本月/上月按自然月
    applyQuickRange(type) {
      const today = this.dayjs()
      let start, end
      switch (type) {
        case 'last7':
          start = today.subtract(6, 'day')
          end = today
          break
        case 'thisWeek':
          start = today.startOf('week').add(1, 'day')
          end = start.add(6, 'day')
          break
        case 'lastWeek':
          start = today.startOf('week').add(1, 'day').subtract(1, 'week')
          end = start.add(6, 'day')
          break
        case 'thisMonth':
          start = today.startOf('month')
          end = today.endOf('month')
          break
        case 'lastMonth':
          start = today.startOf('month').subtract(1, 'month')
          end = start.endOf('month')
          break
        default:
          return
      }
      this.query.startTime = start.format('YYYY-MM-DD') + ' 00:00:00'
      this.query.endTime = end.format('YYYY-MM-DD') + ' 23:59:59'
    },
    contactResultText(v) {
      return { 1: '已接通', 2: '未接通', 3: '无法联系' }[v] || '—'
    },
    handleQuickRangeChange(type) {
      if (type === 'custom') {
        return
      }
      if (type === 'all') {
        // 不传起止时间，后端就不加时间条件
        this.query.startTime = ''
        this.query.endTime = ''
        this.doQueryList({ pageNo: 1 })
        return
      }
      this.applyQuickRange(type)
      this.doQueryList({ pageNo: 1 })
    },
    handleCustomRangeChange(range) {
      if (!range) {
        return
      }
      this.query.startTime = range[0] + ' 00:00:00'
      this.query.endTime = range[1] + ' 23:59:59'
      this.doQueryList({ pageNo: 1 })
    },
    handleQuery() {
      this.doQueryList({ pageNo: 1 })
    },
    handleReset() {
      this.query = { pageNo: 1, pageSize: 10, keyword: '', timeType: 2, startTime: '', endTime: '' }
      this.quickRange = 'last7'
      this.applyQuickRange('last7')
      this.activeStatus = '0'
      this.doQueryList({ pageNo: 1 })
    },
    doQueryList({ pageNo, pageSize }) {
      if (pageNo) this.query.pageNo = pageNo
      if (pageSize) this.query.pageSize = pageSize
      const query = { ...this.query, status: this.activeStatus * 1 }
      // 选了「全部」时起止时间是空串：空串绑到后端的 LocalDateTime 上会报类型转换错，直接不传
      if (!query.startTime || !query.endTime) {
        delete query.startTime
        delete query.endTime
      }
      this.listLoading = true
      return childFollowupApi.fetchList(query)
        .then(({ data }) => {
          // BUG-2026-628461：响应体是 { page: {list,total,...}, metrics: {...} }，
          // 不是把 list/total 拍在 data 顶层。字段名按
          // CustomerChildFollowupListResultVO / PageVO / CustomerChildFollowupMetricsVO 来。
          const page = (data && data.page) || {}
          this.list = page.list || []
          this.total = page.total || 0
          // 汇总指标由同一响应体带出，与列表同口径。
          // ⚠️ 键名用后端 MetricsVO 的原名（total/pending/...），别在这里另起一套再翻译 ——
          //    翻译层就是下一次对不上的地方，这个 bug 本身就是这么来的。
          if (data && data.metrics) {
            this.summary = { ...this.summary, ...data.metrics }
          }
        })
        .finally(() => { this.listLoading = false })
    },
    handleExport() {
      const query = { ...this.query, status: this.activeStatus * 1 }
      this.$router.push({ path: '/export/list', query: { code: 'EEForChildFollowup', ...query } })
    },
    goVisit(id) {
      this.$emit('handleShow', id, 'visit')
    },
    goShow(id) {
      this.$emit('handleShow', id, 'show')
    },
    goActivities(id) {
      this.$emit('handleShow', id, 'activities')
    },
    goCloseRecord(id) {
      this.$emit('handleShow', id, 'closeRecord')
    }
  }
}
</script>

<style lang="scss" scoped>
.range-tip {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}
.summary-row {
  margin-bottom: 4px;
}
.summary-card {
  cursor: pointer;
  text-align: center;
  padding: 12px 0;
  border-radius: 4px;
  background: #f5f7fa;
  &.active {
    background: #ecf5ff;
    border: 1px solid #409eff;
  }
}
.summary-value {
  font-size: 22px;
  font-weight: bold;
  color: #303133;
}
.summary-label {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.sub-status {
  font-size: 12px;
  color: #e73f3f;
}
.btn-right-side {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>

<style>
/* 周报截图模式：隐藏侧边导航与顶部导航（这两个是 Layout 里的兄弟节点，需全局非 scoped 样式才能命中） */
body.followup-screenshot-mode .sidebar-container,
body.followup-screenshot-mode .navbar,
body.followup-screenshot-mode .topmenu-container {
  display: none !important;
}
body.followup-screenshot-mode .main-container {
  margin-left: 0 !important;
}
</style>

